// PATH: src/components/trials/TrialDocumentPicker.tsx
import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDocumentsApi } from "../../api";
import type { CurriculumProgram, DocumentListItem, UUID } from "../../models/domain";
import { EmptyState, StatusPill, errorMessage } from "../ui";
import { MAX_TRIAL_DOCUMENTS, gradeNames, subjectOptions, type SubjectOption } from "./trialLogic";

interface Props {
  programs: CurriculumProgram[];
  subject: SubjectOption | null;
  onSubject: (s: SubjectOption | null) => void;
  selected: UUID[];
  onSelected: (ids: UUID[]) => void;
  disabled?: boolean;
  /** Plafond de l'essai (10 documents pour les faits, 3 chapitres pour les objectifs). */
  max?: number;
  /** Seulement les chapitres importés (essais d'objectifs : extraction par leçon). */
  chaptersOnly?: boolean;
}

/**
 * Étape 1 : la matière, puis 1 à `max` de ses documents (ou de ses chapitres). Un essai ne mêle pas
 * les matières : ses réglages effectifs sont ceux d'UNE matière.
 */
export const TrialDocumentPicker: React.FC<Props> = ({
  programs,
  subject,
  onSubject,
  selected,
  onSelected,
  disabled,
  max = MAX_TRIAL_DOCUMENTS,
  chaptersOnly = false,
}) => {
  const { listDocuments } = useDocumentsApi();
  const options = React.useMemo(() => subjectOptions(programs), [programs]);
  const grades = React.useMemo(() => gradeNames(programs), [programs]);
  const [docs, setDocs] = React.useState<DocumentListItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const seq = React.useRef(0);

  const load = React.useCallback(async () => {
    if (!subject) {
      setDocs(null);
      return;
    }
    const mine = ++seq.current;
    setDocs(null);
    setError(null);
    try {
      const { data } = await listDocuments({
        program_id: subject.program_id,
        subject_id: subject.id,
        order: "program",
        limit: 500,
      });
      if (mine === seq.current) setDocs((data ?? []).filter((d) => !chaptersOnly || Boolean(d.chapter_ref)));
    } catch (e) {
      if (mine === seq.current) setError(errorMessage(e, "Impossible de charger les documents de la matière."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject?.id, chaptersOnly]);

  React.useEffect(() => {
    setQuery("");
    void load();
  }, [load]);

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!docs || !q) return docs ?? [];
    return docs.filter((d) => d.title.toLowerCase().includes(q) || (d.chapter_ref ?? "").toLowerCase().includes(q));
  }, [docs, query]);

  const full = selected.length >= max;
  const toggle = (id: UUID) =>
    onSelected(selected.includes(id) ? selected.filter((x) => x !== id) : full ? selected : [...selected, id]);

  return (
    <Stack spacing={1.5}>
      <Autocomplete
        size="small"
        options={options}
        groupBy={(o) => o.program}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={subject}
        onChange={(_, v) => onSubject(v)}
        disabled={disabled}
        renderInput={(p) => <TextField {...p} label="Matière" placeholder="Choisir une matière" />}
        sx={{ maxWidth: 420 }}
      />

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {subject && !docs && !error && (
        <Box>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={44} />
          ))}
        </Box>
      )}

      {docs && docs.length === 0 && (
        <EmptyState
          dense
          title={chaptersOnly ? "Aucun chapitre importé dans cette matière" : "Aucun document dans cette matière"}
          description="Importez un chapitre depuis l'écran Chapitres, puis revenez ici."
        />
      )}

      {docs && docs.length > 0 && (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              placeholder="Rechercher un chapitre"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Typography variant="body2" color={full ? "warning.dark" : "text.secondary"}>
              {selected.length} / {max} {chaptersOnly ? "chapitres choisis" : "documents choisis"}
              {full ? " : plafond atteint" : ""}
            </Typography>
            {selected.length > 0 && (
              <Button size="small" onClick={() => onSelected([])} disabled={disabled}>
                Tout décocher
              </Button>
            )}
          </Stack>
          <List dense disablePadding sx={{ maxHeight: 320, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
            {visible.map((d) => {
              const checked = selected.includes(d.id);
              return (
                <ListItemButton key={d.id} onClick={() => toggle(d.id)} disabled={disabled || (!checked && full)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" dir="auto" component="span">
                        {d.title}
                      </Typography>
                    }
                    secondary={
                      <Stack component="span" direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {d.chapter_ref && (
                          <Typography component="span" variant="caption" className="mono" color="text.secondary">
                            {d.chapter_ref}
                          </Typography>
                        )}
                        {d.grade_id && grades.get(d.grade_id) && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {grades.get(d.grade_id)}
                          </Typography>
                        )}
                        {!d.is_active && <StatusPill tone="neutral">fermé aux élèves</StatusPill>}
                      </Stack>
                    }
                  />
                </ListItemButton>
              );
            })}
            {visible.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                Aucun chapitre ne correspond.
              </Typography>
            )}
          </List>
        </>
      )}
    </Stack>
  );
};

export default TrialDocumentPicker;
